import type { Page, ProductOperationVo, ProductPagingParams, ProductStatisticAllParams } from "@zhihaoscm/entity";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Dialog as ZhDialog, type TableColumnProps, type TableRequestData } from "zhihao-ui-react";

import { dashboardService } from "@/api/services/dashboardService";
import { formatNumber, toThousand } from "@/utils/number";
import { formatTimeEn } from "@/utils/formatter";
import AdminRequestTable from "@/views/components/AdminRequestTable";

export interface UserGoodsStatisticsDialogProps {
    open: boolean;
    onClose: () => void;
    baseParams?: ProductStatisticAllParams & { productId?: string };
    loadRows?: (
        params: ProductPagingParams & { outerKeyword?: string; productId?: string }
    ) => Promise<Page<ProductOperationVo>>;
}

/** 用户商品统计明细默认接口，沿用旧 Vue custom-paging 契约。 */
export async function loadUserGoodsStatistics(params: ProductPagingParams & { outerKeyword?: string }) {
    return dashboardService.customPaging(params);
}

/** 单商品分析明细使用旧 Vue 的单品分页接口，保留日期与商品 ID 过滤。 */
export async function loadUserGoodsStatisticsByProductId(
    params: ProductPagingParams & { productId?: string; outerKeyword?: string }
) {
    if (!params.productId) {
        return { list: [], currentPage: Number(params.page), pageSize: Number(params.size), total: 0, totalPage: 0 };
    }
    const { outerKeyword, page, size, ...query } = params;
    return dashboardService.customPagingByProductId({
        ...query,
        productId: params.productId,
        page: Number(page),
        size: Number(size),
        keyword: query.keyword || outerKeyword
    });
}

type TablePagingParams = Record<string, unknown>;

type UserGoodsStatisticsTableRow = ProductOperationVo & { __tableRowKey?: string };

/** 为分页结果生成当前页内唯一的行键，避免访客数据缺少用户 ID 时复用 DOM。 */
export const createUserGoodsStatisticsTableRows = (
    list: ProductOperationVo[],
    page: number,
    size: number
): UserGoodsStatisticsTableRow[] =>
    list.map((row, index) => ({
        ...row,
        __tableRowKey: `user-goods-${page}-${size}-${index}`
    }));

/** 读取分页结果的稳定行键，兼容旧接口返回的真实用户标识。 */
export const getUserGoodsStatisticsRowKey = (row: ProductOperationVo) => {
    const tableRow = row as UserGoodsStatisticsTableRow;
    return String(tableRow.__tableRowKey || row.customerId || row.customerCode || "user-goods-row");
};

/** 将 UI 表格分页参数转换为旧接口要求的字符串 page/size。 */
export const normalizeUserGoodsStatisticsPagingParams = (
    params: TablePagingParams
): ProductPagingParams & { outerKeyword?: string } => ({
    ...(params as Omit<ProductPagingParams, "page" | "size"> & { outerKeyword?: string }),
    page: String(params.page ?? 1),
    size: String(params.size ?? 10)
});

/** 组合外层商品筛选条件和明细表分页条件，保持旧 Vue 请求字段命名。 */
export const buildUserGoodsStatisticsQuery = (
    rawParams: TablePagingParams,
    baseParams?: ProductStatisticAllParams & { productId?: string }
): ProductPagingParams & { outerKeyword?: string; productId?: string } => {
    const params = normalizeUserGoodsStatisticsPagingParams(rawParams);
    const { keyword, ...outerParams } = baseParams || {};
    return {
        ...outerParams,
        ...params,
        scope: outerParams.scope === 6 ? undefined : outerParams.scope,
        ...(keyword ? { outerKeyword: keyword } : {})
    };
};

const displayNumber = (value?: number) => (value === 0 ? 0 : formatNumber(value, 2));

/** 用户商品明细表列，保持 Vue 原有字段、排序字段和用户跳转入口。 */
const createColumns = (navigate: ReturnType<typeof useNavigate>): TableColumnProps<ProductOperationVo>[] => [
    {
        prop: "customerCode",
        label: "用户ID",
        minWidth: 140,
        search: {
            order: 1,
            el: "input",
            props: { placeholder: "请输入用户ID/手机号/个人实名/机构名称搜索", maxlength: 50 },
            key: "keyword"
        },
        render: ({ row }) => (
            <button
                type="button"
                className="text-blue-600 hover:underline"
                onClick={() => navigate(`/lianYunUser/userInfo?userId=${row.customerId ?? ""}`)}
            >
                {row.customerCode || "--"}
            </button>
        )
    },
    { prop: "name", label: "个人实名/机构名称", minWidth: 200 },
    { prop: "mobile", label: "注册手机号", minWidth: 135 },
    {
        prop: "totalSalesAmount",
        label: "总销售额(元)",
        minWidth: 135,
        sortable: "custom",
        render: ({ row }) => displayNumber(row.totalSalesAmount)
    },
    {
        prop: "settlementAmount",
        label: "结算金额(元)",
        minWidth: 135,
        sortable: "custom",
        render: ({ row }) => displayNumber(row.settlementAmount)
    },
    {
        prop: "refundAmount",
        label: "退款金额(元)",
        minWidth: 135,
        sortable: "custom",
        render: ({ row }) => displayNumber(row.refundAmount)
    },
    {
        prop: "totalSalesVolume",
        label: "总销量(吨)",
        minWidth: 120,
        sortable: "custom",
        render: ({ row }) => displayNumber(row.totalSalesVolume)
    },
    {
        prop: "actualSettledTons",
        label: "实际结算吨数(吨)",
        minWidth: 160,
        sortable: "custom",
        render: ({ row }) => displayNumber(row.actualSettledTons)
    },
    {
        prop: "productVisitCount",
        label: "商品访问次数(次)",
        minWidth: 160,
        sortable: "custom",
        render: ({ row }) => toThousand(row.productVisitCount)
    },
    {
        prop: "systemTotalStayTime",
        label: "系统停留时长",
        minWidth: 160,
        sortable: "custom",
        render: ({ row }) => formatTimeEn(row.systemTotalStayTime)
    },
    {
        prop: "totalStayTimeSec",
        label: "商品停留时长",
        minWidth: 160,
        sortable: "custom",
        render: ({ row }) => formatTimeEn(row.totalStayTimeSec)
    },
    {
        prop: "avgSingleStayTime",
        label: "平均单次停留时长",
        minWidth: 160,
        sortable: "custom",
        render: ({ row }) => formatTimeEn(row.avgSingleStayTime)
    }
];

/** 用户商品统计明细弹窗，复用 zhihao-ui-react Table/Dialog 并保留 Vue 的 1200 宽度。 */
export function UserGoodsStatisticsDialog({
    open,
    onClose,
    baseParams,
    loadRows = loadUserGoodsStatistics
}: UserGoodsStatisticsDialogProps) {
    const navigate = useNavigate();
    const columns = createColumns(navigate);
    const [sort, setSort] = useState<{ key: string; order: "asc" | "desc" }>();

    // 弹窗关闭后清除排序，下一次打开沿用 Vue 的第一页默认查询。
    useEffect(() => {
        if (!open) setSort(undefined);
    }, [open]);

    const requestRows = useCallback(
        async (rawParams: unknown): Promise<TableRequestData<ProductOperationVo>> => {
            const params = buildUserGoodsStatisticsQuery((rawParams || {}) as TablePagingParams, baseParams);
            if (sort) {
                params.sortKey = sort.key;
                params.sortOrder = sort.order;
            }
            const result = await loadRows(params);
            const page = Number(params.page);
            const size = Number(params.size);
            return {
                list: createUserGoodsStatisticsTableRows(result.list || [], page, size),
                currentPage: page,
                pageSize: size,
                total: result.total || 0
            };
        },
        [baseParams, loadRows, sort]
    );

    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order?: "ascending" | "descending" | null }) => {
            setSort(prop && order ? { key: prop, order: order === "ascending" ? "asc" : "desc" } : undefined);
        },
        []
    );

    return (
        <ZhDialog open={open} title="用户商品统计明细" width={1200} onCancel={onClose} footer={null}>
            <AdminRequestTable<ProductOperationVo>
                key={`${open}-${sort?.key || ""}-${sort?.order || ""}`}
                requestApi={requestRows}
                columns={columns}
                rowKey={getUserGoodsStatisticsRowKey}
                initParam={{ page: 1, size: 10 }}
                requestAuto={open}
                onSortChange={handleSortChange}
            />
        </ZhDialog>
    );
}

export default UserGoodsStatisticsDialog;
